import { getVerdiConfiguration } from "verdi-common";

import {
    VerificationCodeByStatus,
    VerificationStatus,
    VerificationStatusByCode,
} from "verdi-common/schemas/device/subSchemas/sproutVerification/generalSproutVerificationCheck";
import type DeviceMapEntity from "../../../map-management/map-entities/device-map-entity";
import { VERIFICATION_STATE, VERIFICATION_STATUS_TO_VERIFICATION_STATE } from "./definitions";

/**
 * Returns a verification state for a sprout (gen3) or gen4 device
 *
 * As of Jun 9, 2025, despite the name indicating sprout verification, both sprout & gen4 devices
 * have the same model.sproutIrrigationVerification property and use the same getters.
 *
 * We have a ticket to potentially rename and clean up the naming
 * ref: https://linear.app/verdiag/issue/SWD-1579/rename-the-sproutverification-component-to-be-more-generic
 */
export const getSproutVerificationState = ({ model }: { model: DeviceMapEntity["model"] }) => {
    const sproutIrrigationVerification = model?.sproutIrrigationVerification;
    if (!sproutIrrigationVerification) {
        return VERIFICATION_STATE.UNKNOWN;
    }
    // Find state:
    const doubleValveDevice = getVerdiConfiguration(model.type).valveCount === 2;
    // The keys are binary representations of valve state, eg 0b00 is both valves closed, 0b01 is V1 open V2 closed, etc
    const verificationsOfImportance = doubleValveDevice ? [0b00, 0b01, 0b10, 0b11] : [0b00, 0b11];

    const maxStatusScore = verificationsOfImportance.reduce((previousScore, verificationIndex) => {
        const ver = sproutIrrigationVerification.verificationsByValveState[verificationIndex];
        if (!ver) {
            return previousScore;
        }

        let status: VerificationStatus = "error";
        if (ver.pendingData) {
            status = "pending" as VerificationStatus;
        } else if (ver.successful) {
            status = "successful" as VerificationStatus;
        }

        // Convert the previous score to a string to avoid type errors
        const previousStatusScore = VerificationCodeByStatus[VerificationStatusByCode[previousScore]] || -1000;
        const currentStatusScore = VerificationCodeByStatus[status];

        const localMaxStatusScore = Math.max(previousStatusScore, currentStatusScore);

        return localMaxStatusScore;
    }, 0);

    return VERIFICATION_STATUS_TO_VERIFICATION_STATE[VerificationStatusByCode[maxStatusScore]];
};
